import { useEffect, useRef, useState } from "react";
import EmbedFlow from "@formsort/react-embed";
import { createFileRoute } from "@tanstack/react-router";
import { v7 as uuidv7 } from "uuid";

import { GuestHeader, TimedLoader } from "@/components";
import { analytics } from "@/config/analytics";
import { PROFILING_ERRORS } from "@/domains/profile/models/constants";
import { profilingUrlSchema } from "@/domains/profile/models/schemas";
import { env } from "@/env";
import { useMetaAttribution, usePersonify } from "@/hooks";
import { Heading } from "@/ui";
import { useProfilingQueryParams } from "./-hooks/use-profiling-query-params";
import { ResumeError } from "./-components";

const stepsForNotEligible = [
  "under_21",
  "pregnant_or_breastfeeding",
  "not_in_mental_condition_treatment",
];

const Profiling = () => {
  const navigate = Route.useNavigate();
  const { error, responder_uuid } = Route.useSearch();

  const responderUuid = useRef(responder_uuid ?? uuidv7()).current;
  const [loadCount, setLoadCount] = useState(0);

  useMetaAttribution();

  const {
    awaitingWebhook,
    isExchangeLoading,
    onboardingToken,
    queryParams: personifyQueryParams,
    setAwaitingWebhook,
  } = usePersonify();

  const profilingQueryParams = useProfilingQueryParams(personifyQueryParams);

  useEffect(() => {
    if (responder_uuid) {
      void navigate({
        to: "/profiling",
        search: ({ responder_uuid: _removed, ...rest }) => {
          return rest;
        },
        replace: true,
      });
    }
  }, [navigate, responder_uuid]);

  /**
   * This forces the responder UUID to be regenerated
   * for those cases where the flow is restarted. If
   * this isn't done, it results in the password being
   * set to the user record associated to the previous
   * responder UUID.
   */
  useEffect(() => {
    if (loadCount > 1) {
      window.location.reload();
    }
  }, [loadCount]);

  // Gate 1: wait for the onboarding-token exchange before rendering the embed.
  if (onboardingToken && isExchangeLoading) {
    return (
      <TimedLoader shouldAutoComplete={false}>
        <Heading className="text-xl sm:text-3xl" level={1}>
          Preparing your profile…
        </Heading>
      </TimedLoader>
    );
  }

  // Gate 2 (OIDC only): Formsort finished — wait for the webhook to be
  // processed, then auto-redirect into the silent OIDC sign-in round-trip.
  if (awaitingWebhook) {
    return (
      <TimedLoader shouldAutoComplete={false}>
        <Heading className="text-xl sm:text-3xl" level={1}>
          Setting up your account…
        </Heading>
      </TimedLoader>
    );
  }

  return (
    <div className="flex h-dvh w-full flex-col">
      <GuestHeader logoRedirectUrl={env.VITE_MARKETING_SITE_DOMAIN} />
      {error === PROFILING_ERRORS.RESUME ? (
        <ResumeError />
      ) : (
        <div className="flex-1">
          <EmbedFlow
            clientLabel={env.VITE_FORMSORT_PROFILING_CLIENT_LABEL}
            embedConfig={{ style: { width: "100%", height: "100%" } }}
            flowLabel={env.VITE_FORMSORT_PROFILING_FLOW_LABEL}
            formsortEnv={env.VITE_FORMSORT_PROFILING_ENV}
            onFlowFinalized={({ stepId }) => {
              if (stepId !== undefined && !stepsForNotEligible.includes(stepId)) {
                analytics.track("flow_finalized", {
                  flow_label: env.VITE_FORMSORT_PROFILING_FLOW_LABEL,
                  responder_uuid: responderUuid,
                });
                if (onboardingToken) {
                  // OIDC path: no password step. Poll until the profiling webhook
                  // has been processed, then start the silent sign-in round-trip.
                  setAwaitingWebhook(true);
                } else {
                  navigate({ to: "/profiling/password", search: { id: responderUuid } });
                }
              }
            }}
            onFlowLoaded={() => {
              analytics.track("flow_loaded", {
                flow_label: env.VITE_FORMSORT_PROFILING_FLOW_LABEL,
                responder_uuid: responderUuid,
              });
              setLoadCount((prev) => {
                return prev + 1;
              });
            }}
            onStepCompleted={({ stepId, stepIndex }) => {
              if (!stepId || stepIndex === undefined) {
                return;
              }
              analytics.track("step_completed", {
                flow_label: env.VITE_FORMSORT_PROFILING_FLOW_LABEL,
                responder_uuid: responderUuid,
                step_id: stepId,
                step_index: stepIndex,
              });
            }}
            queryParams={profilingQueryParams}
            responderUuid={responderUuid}
            variantLabel={env.VITE_FORMSORT_PROFILING_VARIANT_LABEL}
          />
        </div>
      )}
    </div>
  );
};

export const Route = createFileRoute("/_guest/profiling/")({
  component: Profiling,
  validateSearch: profilingUrlSchema,
});
